import { Sphere } from 'types/global';
import { Schema } from 'types/schema';
import { PerspectiveAssignment } from '../initializeSphere';

/**
 * Helper to check if a sphere matches perspective assignee filters
 */
function matchesAssigneeFilter(sphere: Sphere, assigneeFilter: any[], schemas: Schema[]): boolean {
  if (!assigneeFilter || assigneeFilter.length === 0) return false;

  // Get the sphere's schema to access node_label
  const sphereType = Array.isArray(sphere.properties.type) ? sphere.properties.type[0] : sphere.properties.type;
  const sphereSchema = schemas.find((s) => s.system_name === sphereType);

  // assignee_filter uses OR logic (any filter can match)
  return assigneeFilter.some((filter) => {
    // Special handling for 'label' property - it refers to the schema's node_label
    if (filter.property === 'label') {
      const nodeLabel = sphereSchema?.node_label;
      if (filter.operator === 'equals') {
        return nodeLabel === filter.value;
      }
      return false;
    }

    // Special handling for 'type' property - it refers to properties.type (system_name)
    if (filter.property === 'type') {
      if (filter.operator === 'equals') {
        return sphereType === filter.value;
      }
      return false;
    }

    // For other properties, traverse the sphere object
    const propertyPath = filter.property.split('.');
    let value: any = sphere;

    for (const key of propertyPath) {
      value = value?.[key];
    }

    // Handle array values (e.g., properties.type might be an array)
    if (Array.isArray(value)) {
      value = value[0];
    }

    if (filter.operator === 'equals') {
      return value === filter.value;
    }

    return false;
  });
}

/**
 * Resolves the `center_center` assignment: the parent if it matches the filter, else the
 * parent's own resolved `center_center`. Returns whether the parent itself matched, since the
 * `center_parent` resolution below must skip when it did (a sphere can't be both).
 */
function resolveCenterCenterForBuild(
  parentSphere: Sphere,
  centerCenterPerspective: ReturnType<Schema['perspectives']['find']>,
  schemas: Schema[],
  parentType: unknown,
  parentNodeLabel: string
): { assignment: PerspectiveAssignment | undefined; matched: boolean } {
  if (!centerCenterPerspective) return { assignment: undefined, matched: false };

  console.log('[buildPerspectiveAssignments] Checking center_center filter:', {
    assigneeFilterRules: centerCenterPerspective.assignee_filter,
    parentVertexLabel: parentSphere.label,
    parentType,
    parentNodeLabel,
  });

  if (matchesAssigneeFilter(parentSphere, centerCenterPerspective.assignee_filter, schemas)) {
    console.log('[buildPerspectiveAssignments] ✅ Parent matches center_center filter, assigned to center_center:', {
      parentId: parentSphere.id,
      parentVertexLabel: parentSphere.label,
      parentType,
      parentNodeLabel,
      parentName: parentSphere.properties.name,
    });
    return { assignment: { perspectiveSystemName: 'center_center', assignedSphere: parentSphere }, matched: true };
  }

  // Parent doesn't match → extract parent's center_center
  const parentCenterAssignment = parentSphere.assignedPerspectives?.find(
    (ap) => ap.perspectiveProperties.system_name === 'center_center'
  );

  if (parentCenterAssignment?.assigned?.[0]?.sphere) {
    const centerSphere = parentCenterAssignment.assigned[0].sphere;
    const centerType = Array.isArray(centerSphere.properties.type) ? centerSphere.properties.type[0] : centerSphere.properties.type;
    const centerSchema = schemas.find((s) => s.system_name === centerType);
    const centerNodeLabel = centerSchema?.node_label || 'unknown';

    console.log('[buildPerspectiveAssignments] ❌ Parent doesn\'t match center_center filter, using parent\'s center:', {
      parentId: parentSphere.id,
      parentVertexLabel: parentSphere.label,
      parentType,
      parentNodeLabel,
      parentName: parentSphere.properties.name,
      centerId: centerSphere.id,
      centerVertexLabel: centerSphere.label,
      centerType,
      centerNodeLabel,
      centerName: centerSphere.properties.name,
    });
    return { assignment: { perspectiveSystemName: 'center_center', assignedSphere: centerSphere }, matched: false };
  }

  console.warn('[buildPerspectiveAssignments] Parent doesn\'t match center_center filter and has no center_center assigned:', {
    parentId: parentSphere.id,
    parentVertexLabel: parentSphere.label,
    parentType,
    parentNodeLabel,
    parentName: parentSphere.properties.name,
    hasAssignedPerspectives: !!parentSphere.assignedPerspectives,
    assignedPerspectivesCount: parentSphere.assignedPerspectives?.length || 0,
  });
  return { assignment: undefined, matched: false };
}

/**
 * Resolves the `center_parent` assignment: the parent, if it matches the filter — skipped
 * entirely when the parent already matched `center_center` above (a sphere can't be both) or
 * the caller wants the sphere unplaced.
 */
function resolveCenterParentForBuild(
  parentSphere: Sphere,
  centerParentPerspective: ReturnType<Schema['perspectives']['find']>,
  schemas: Schema[],
  parentType: unknown,
  parentNodeLabel: string,
  parentMatchedCenterCenterFilter: boolean,
  unplaced: boolean | undefined
): PerspectiveAssignment | undefined {
  if (!centerParentPerspective) return undefined;

  if (parentMatchedCenterCenterFilter) {
    console.log('[buildPerspectiveAssignments] ⏭️ Parent matched center_center filter, skipping center_parent check (cannot be both):', {
      parentId: parentSphere.id,
      parentVertexLabel: parentSphere.label,
      parentType,
      parentNodeLabel,
      parentName: parentSphere.properties.name,
    });
    return undefined;
  }
  if (unplaced) return undefined;

  console.log('[buildPerspectiveAssignments] Checking center_parent filter:', {
    assigneeFilterRules: centerParentPerspective.assignee_filter,
    parentVertexLabel: parentSphere.label,
    parentType,
    parentNodeLabel,
  });

  if (matchesAssigneeFilter(parentSphere, centerParentPerspective.assignee_filter, schemas)) {
    console.log('[buildPerspectiveAssignments] ✅ Parent matches center_parent filter, assigned to center_parent:', {
      parentId: parentSphere.id,
      parentVertexLabel: parentSphere.label,
      parentType,
      parentNodeLabel,
      parentName: parentSphere.properties.name,
    });
    return { perspectiveSystemName: 'center_parent', assignedSphere: parentSphere };
  }

  console.log('[buildPerspectiveAssignments] ❌ Parent doesn\'t match center_parent filter, skipping:', {
    parentId: parentSphere.id,
    parentVertexLabel: parentSphere.label,
    parentType,
    parentNodeLabel,
    parentName: parentSphere.properties.name,
  });
  return undefined;
}

/**
 * Builds perspective assignments for a new sphere based on its parent and schema.
 *
 * This helper implements the standard perspective assignment logic by checking
 * the schema's perspective assignee_filter rules:
 * - Check if parent matches center_center assignee_filter → if yes, assign parent to center_center
 * - If parent doesn't match → extract parent's center_center perspective
 * - Check if parent matches center_parent assignee_filter → if yes, assign parent to center_parent
 *
 * @param schema - The schema for the sphere being created
 * @param parentSphere - The parent sphere for the new sphere being created (must have assignedPerspectives populated)
 * @param schemas - All schemas (needed to look up node_label for filter matching)
 * @returns Array of perspective assignments to pass to initializeSphere
 *
 * @example
 * // Creating a Pillar under a Center
 * const assignments = buildPerspectiveAssignments(pillarSchema, centerSphere, schemas);
 * // Returns: [{ perspectiveSystemName: 'center_center', assignedSphere: centerSphere }]
 *
 * @example
 * // Creating a Priority under a Pillar
 * const assignments = buildPerspectiveAssignments(prioritySchema, pillarSphere, schemas);
 * // Returns: [
 * //   { perspectiveSystemName: 'center_center', assignedSphere: centerSphere },
 * //   { perspectiveSystemName: 'center_parent', assignedSphere: pillarSphere }
 * // ]
 */
export function buildPerspectiveAssignments(
  schema: Schema,
  parentSphere: Sphere,
  schemas: Schema[],
  /** When true, skip the center_parent auto-assign below — the caller wants the sphere unplaced. */
  unplaced?: boolean
): PerspectiveAssignment[] {
  const perspectiveAssignments: PerspectiveAssignment[] = [];

  // Find center_center and center_parent perspectives in schema
  const centerCenterPerspective = schema.perspectives?.find((p) => p.system_name === 'center_center');
  const centerParentPerspective = schema.perspectives?.find((p) => p.system_name === 'center_parent');

  // Look up parent's schema to get its node_label for logging
  const parentType = Array.isArray(parentSphere.properties.type) ? parentSphere.properties.type[0] : parentSphere.properties.type;
  const parentSchema = schemas.find((s) => s.system_name === parentType);
  const parentNodeLabel = parentSchema?.node_label || 'unknown';

  // Handle center_center assignment
  const { assignment: centerCenterAssignment, matched: parentMatchedCenterCenterFilter } = resolveCenterCenterForBuild(
    parentSphere,
    centerCenterPerspective,
    schemas,
    parentType,
    parentNodeLabel
  );
  if (centerCenterAssignment) perspectiveAssignments.push(centerCenterAssignment);

  // Handle center_parent assignment.
  // IMPORTANT: Only assign parent to center_parent if it did NOT match the center_center filter
  // A sphere cannot be both center_center and center_parent
  const centerParentAssignment = resolveCenterParentForBuild(
    parentSphere,
    centerParentPerspective,
    schemas,
    parentType,
    parentNodeLabel,
    parentMatchedCenterCenterFilter,
    unplaced
  );
  if (centerParentAssignment) perspectiveAssignments.push(centerParentAssignment);

  return perspectiveAssignments;
}
